import { captureError } from '@leagueplatform/observability';
import { trackAnalyticsEvent } from './analytics';
import type {
  DomainPrefixedEventKey,
  PlaceholdersByEventKey,
} from './analytics-events/analytics-events.types';

type DomainModule = { events: Record<string, any> };

const ASYNC_DOMAIN_REGISTRY: Record<string, () => Promise<DomainModule>> = {
  about_me: () => import('./analytics-events/about_me').then((m) => m.about_me),
  activities: () =>
    import('./analytics-events/activities').then((m) => m.activities),
  care_collections: () =>
    import('./analytics-events/care_collections').then(
      (m) => m.care_collections,
    ),
  challenges: () =>
    import('./analytics-events/challenges').then((m) => m.challenges),
  dashboard: () =>
    import('./analytics-events/dashboard').then((m) => m.dashboard),
  devices: () => import('./analytics-events/devices').then((m) => m.devices),
  journey: () => import('./analytics-events/journey').then((m) => m.journey),
  program: () => import('./analytics-events/program').then((m) => m.program),
  rewards: () => import('./analytics-events/rewards').then((m) => m.rewards),
  wallet: () => import('./analytics-events/wallet').then((m) => m.wallet),
};

async function loadDomainEvents(domain: string): Promise<Record<string, any>> {
  if (!(domain in ASYNC_DOMAIN_REGISTRY)) {
    return {};
  }

  try {
    const { events } = await ASYNC_DOMAIN_REGISTRY[domain]();
    return events;
  } catch (error) {
    captureError(
      new Error(`Failed to load analytics domain "${domain}": ${error}`),
    );
    return {};
  }
}

const removeEmptyStrings = (
  obj: Record<string, unknown>,
): Record<string, unknown> =>
  Object.entries(obj).reduce((acc, [key, value]) => {
    if (value === '') return acc;

    if (value && typeof value === 'object' && !Array.isArray(value)) {
      const cleaned = removeEmptyStrings(value as Record<string, unknown>);
      if (Object.keys(cleaned).length > 0) acc[key] = cleaned;
    } else {
      acc[key] = value;
    }

    return acc;
  }, {} as Record<string, unknown>);

async function fireEvent<Key extends DomainPrefixedEventKey>(
  eventKey: Key,
  ...args: PlaceholdersByEventKey[Key] extends never
    ? []
    : [placeholders: Record<NonNullable<PlaceholdersByEventKey[Key]>, string>]
) {
  const parts = eventKey.split('.');
  const domain = parts[0];
  const eventId = parts.slice(1).join('.');

  const domainEvents = await loadDomainEvents(domain);

  // eslint-disable-next-line @typescript-eslint/naming-convention
  const {
    optional,
    metadata,
    event_name: eventName,
    ...event
  } = domainEvents[eventId] || {};

  const mergedEvent = { ...(optional ?? {}), ...event };

  const placeholders = args[0];
  if (!placeholders) {
    const cleanedEvent = removeEmptyStrings(mergedEvent);
    trackAnalyticsEvent(eventName, cleanedEvent);
    return;
  }

  let stringifiedEvent = JSON.stringify(mergedEvent);

  Object.entries(
    placeholders as Record<PlaceholdersByEventKey[Key], string>,
  ).forEach(([property, value]) => {
    if (typeof value === 'string')
      stringifiedEvent = stringifiedEvent.replace(
        // eslint-disable-next-line no-useless-escape
        new RegExp(`%\{${property}\}`, 'g'),
        value,
      );
  });

  const finalEvent = JSON.parse(stringifiedEvent);
  const cleanedFinalEvent = removeEmptyStrings(finalEvent);
  trackAnalyticsEvent(eventName, cleanedFinalEvent);
}

/**
 * Send a standardized analytics event.
 *
 * Event dispatch is asynchronous: the domain module is lazily loaded on first
 * use and cached by the module loader thereafter.
 *
 * @param eventKey - The domain-prefixed event key.
 * @param placeholders - An object containing the values to replace the placeholders in the event.
 */
export function track<Key extends DomainPrefixedEventKey>(
  eventKey: Key,
  ...args: PlaceholdersByEventKey[Key] extends never
    ? []
    : [placeholders: Record<NonNullable<PlaceholdersByEventKey[Key]>, string>]
): void {
  fireEvent(eventKey, ...args).catch((error) => {
    captureError(new Error(`Analytics track("${eventKey}") failed: ${error}`));
  });
}
